import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getConsultation, getPatient } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ArrowLeft, GraduationCap, Loader2, Scale, UserRound } from "lucide-react";
import { toast } from "sonner";

const ESCALA_ETIQUETA = {
  barthel: "Índice de Barthel",
  katz: "Índice de Katz",
  lawton: "Escala de Lawton",
  mmse: "Mini-Mental (MMSE)",
  pfeiffer: "Cuestionario de Pfeiffer",
  yesavage: "Escala de Yesavage (GDS)",
  gds_yesavage: "Escala de Yesavage (GDS)",
  morse: "Escala de Morse",
  tinetti: "Escala de Tinetti",
  frail: "Escala FRAIL",
  mna: "Mini Nutritional Assessment (MNA)",
  charlson: "Índice de Charlson",
  norton: "Escala de Norton",
  gijon: "Escala de Gijón",
};

function computeAgeFromBirthDate(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const born = new Date(fechaNacimiento);
  if (Number.isNaN(born.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - born.getFullYear();
  const monthDiff = today.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < born.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

function patientDisplayAge(patient) {
  if (patient?.edad != null && patient.edad !== "" && patient.edad !== "—") return patient.edad;
  return computeAgeFromBirthDate(patient?.fecha_nacimiento);
}

function patientFullName(patient) {
  return [patient?.nombres, patient?.apellidos].filter(Boolean).join(" ").trim() || "Paciente";
}

function formatPracticeDate(value) {
  if (!value) return "Fecha no disponible";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";
  return date.toLocaleDateString("es-GT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function consultationDate(consultation) {
  return consultation?.fecha_consulta || consultation?.created_at || null;
}

function scaleLabel(key) {
  return (ESCALA_ETIQUETA[key] || key.replace(/_/g, " ")).toUpperCase();
}

function interpretacionTone(interpretacion) {
  const t = (interpretacion || "").toLowerCase();
  if (!t) return "neutral";
  if (/sever|alto riesgo|alta comorbilidad|frágil|fragil|depresión mayor|dependencia total|riesgo alto/.test(t)) {
    return "severe";
  }
  if (/moderad|probable|riesgo|dependencia/.test(t)) {
    return "moderate";
  }
  if (/normal|independ|robusto|leve|escasa|bajo|envejecimiento/.test(t)) {
    return "mild";
  }
  return "neutral";
}

const TONE_STYLES = {
  severe: "border-red-200 bg-red-50 ring-red-100",
  moderate: "border-amber-200 bg-amber-50 ring-amber-100",
  mild: "border-emerald-200 bg-emerald-50 ring-emerald-100",
  neutral: "border-violet-200 bg-violet-50 ring-violet-100",
};

function ClinicalField({ label, value }) {
  if (!value) return null;
  return (
    <p className="text-sm leading-relaxed text-slate-700">
      <span className="font-semibold text-slate-900">{label}: </span>
      {value}
    </p>
  );
}

export default function AulaPracticaDetalle() {
  const { consultation_id: consultationId } = useParams();
  const [consultation, setConsultation] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!consultationId) {
      setError("Práctica no encontrada");
      setLoading(false);
      return;
    }

    let alive = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const consultRes = await getConsultation(consultationId);
        if (!alive) return;

        const c = consultRes?.data ?? consultRes;
        if (!c) {
          setError("Práctica no encontrada");
          return;
        }

        const patientId = (c.paciente_id || c.patient_id || "").trim();
        if (!patientId) {
          setConsultation(c);
          setError("No se encontró el paciente asociado a esta práctica");
          return;
        }

        const patientRes = await getPatient(patientId);
        if (!alive) return;

        const p = patientRes?.data ?? patientRes;
        if (!p) {
          setConsultation(c);
          setError("Paciente no encontrado");
          return;
        }

        setConsultation(c);
        setPatient(p);
      } catch {
        if (alive) {
          setError("No se pudo cargar la práctica");
          toast.error("Error al cargar la práctica");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [consultationId]);

  if (loading) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-violet-700">
        <Loader2 className="h-9 w-9 animate-spin" aria-hidden />
        <p className="text-sm font-medium">Cargando práctica...</p>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Card className="border-violet-200 bg-white">
          <CardContent className="py-10 text-center">
            <p className="text-base font-semibold text-slate-900">{error || "Práctica no encontrada"}</p>
            <Button asChild variant="outline" className="mt-4 border-violet-200">
              <Link to="/aula/practicas">Volver a mis prácticas</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const age = patient ? patientDisplayAge(patient) : null;
  const escalas = consultation?.escalas && typeof consultation.escalas === "object" ? consultation.escalas : {};
  const scaleEntries = Object.entries(escalas).filter(([, v]) => v != null);

  return (
    <div className="mx-auto max-w-4xl space-y-6 animate-fade-in pb-8">
      {/* Sección 1 — Header */}
      <Card className="border-violet-200/80 bg-white shadow-sm">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-700">
                <UserRound className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div>
                <CardTitle className="text-xl text-slate-900">
                  {patient ? patientFullName(patient) : "Paciente ficticio"}
                </CardTitle>
                <p className="mt-1 text-sm text-slate-600">
                  {age != null ? `${age} años` : "Edad no disponible"}
                </p>
                {patient?.diagnostico_principal && (
                  <p className="mt-2 text-sm text-slate-700">
                    <span className="font-semibold text-slate-900">Diagnóstico principal: </span>
                    {patient.diagnostico_principal}
                  </p>
                )}
                <p className="mt-2 text-sm text-slate-500">
                  {formatPracticeDate(consultationDate(consultation))}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Button asChild variant="ghost" className="text-violet-700 hover:bg-violet-50 hover:text-violet-900 -ml-2">
            <Link to="/aula/practicas">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a mis prácticas
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Sección 2 — Datos de la consulta */}
      <Card className="border-violet-200/80 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-slate-900">Datos de la consulta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ClinicalField label="Motivo de consulta" value={consultation.motivo_consulta} />
          <ClinicalField label="Historia de la enfermedad actual" value={consultation.historia_enfermedad_actual} />
          <ClinicalField label="Impresión clínica" value={consultation.impresion_clinica} />
          <ClinicalField label="Diagnóstico" value={consultation.diagnostico} />
          <ClinicalField label="Plan terapéutico" value={consultation.plan_terapeutico} />
          {!consultation.motivo_consulta &&
            !consultation.historia_enfermedad_actual &&
            !consultation.impresion_clinica &&
            !consultation.diagnostico &&
            !consultation.plan_terapeutico && (
              <p className="text-sm text-slate-500">No hay datos clínicos registrados en esta práctica.</p>
            )}
        </CardContent>
      </Card>

      {/* Sección 3 — Escalas aplicadas */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900">Escalas aplicadas</h2>
        </div>
        {scaleEntries.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {scaleEntries.map(([key, raw]) => {
              const score = raw && typeof raw === "object" ? (raw.score ?? raw.puntaje ?? raw.value) : raw;
              const interpretacion =
                raw && typeof raw === "object" ? raw.interpretacion || raw.interpretación : null;
              const tone = interpretacionTone(interpretacion);
              return (
                <Card key={key} className={`ring-1 shadow-sm ${TONE_STYLES[tone]}`}>
                  <CardContent className="p-4">
                    <p className="text-xs font-bold tracking-wide text-slate-700">{scaleLabel(key)}</p>
                    <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{score ?? "—"}</p>
                    {interpretacion && (
                      <p className="mt-2 text-sm font-medium text-slate-700">{interpretacion}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-violet-200/80 bg-white shadow-sm">
            <CardContent className="py-6 text-center">
              <p className="text-sm text-slate-500">No se aplicaron escalas en esta práctica</p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Sección 4 — Nota al pie */}
      <Card className="border-violet-200 bg-violet-50/60 shadow-sm">
        <CardContent className="p-5">
          <div className="flex gap-3">
            <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
            <p className="text-sm leading-relaxed text-violet-950">
              Esta es tu práctica personal. Los datos son ficticios y no forman parte de ningún expediente
              clínico real.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
